<template>
  <v-layout>
    <!-- DESKTOP APPBAR -->
    <TheAppBar
      v-model:support-panel="supportPanel"
      hide-app-switch
      hide-logo
      :is-mobile="device?.isMobile"
    >
      <template v-if="deepBoxPreviewStore.currentNodeData" #desktop-prepend-end>
        <v-btn
          :icon="mdAndUp ? undefined : 'far fa-arrow-left'"
          :prepend-icon="mdAndUp ? 'far fa-arrow-left' : undefined"
          :text="
            mdAndUp
              ? isBoxPrivate && !!currentGenericFolder
                ? currentGenericFolder?.displayName
                : originRoute.text
              : undefined
          "
          :to="originRoute.to"
          variant="text"
        />
      </template>

      <template v-if="deepBoxPreviewStore.currentNodeData" #desktop-center>
        <div
          v-if="
            deepBoxPreviewStore.currentNodeData &&
            deepBoxPreviewStore.currentNodeData.node.displayName
          "
          class="overflow-hidden d-flex flex-fill justify-center align-center"
        >
          <v-icon start>
            <NodeIcon
              :mime-type="deepBoxPreviewStore.currentNodeData.node.mimeType"
              :name="deepBoxPreviewStore.currentNodeData.node.name"
              :type="deepBoxPreviewStore.currentNodeData.node.type"
            />
          </v-icon>
          <DeepTextTruncateMiddle>
            {{ deepBoxPreviewStore.currentNodeData.node.displayName }}
          </DeepTextTruncateMiddle>
        </div>
      </template>

      <template v-if="smAndUp" #desktop-append>
        <!-- LOADING -->
        <v-btn
          v-if="
            deepBoxPreviewStore.fetchParentNodeChildrenPending &&
            currentFileIndex === -1
          "
          class="mr-3"
          density="compact"
          icon
          :loading="
            deepBoxPreviewStore.fetchParentNodeChildrenPending &&
            currentFileIndex === -1
          "
          variant="flat"
        />

        <!-- PREV/NEXT -->
        <div
          v-else-if="currentFileIndex >= 0"
          class="d-flex flex-row align-center ga-2 mr-3"
        >
          <div>
            <v-btn
              v-if="currentFileIndex >= 1"
              density="compact"
              exact
              icon="far fa-chevron-left"
              :to="previousItemRoute"
              variant="flat"
            />
          </div>

          <div v-if="currentFileIndex === -1">
            <!-- Still loading the slice that contains this file -->
            <v-progress-circular indeterminate size="16" />
          </div>
          <div v-else class="text-no-wrap">
            {{
              t('navigation.of', {
                currentFile: displayIndex + 1,
                filesNumber: totalFilesSize,
              })
            }}
          </div>

          <div>
            <v-btn
              v-if="currentFileIndex < totalFilesSize - 1"
              density="compact"
              exact
              icon="far fa-chevron-right"
              :loading="isFetchingNextNodes"
              :to="nextItemRoute"
              variant="flat"
            />
          </div>
        </div>
      </template>
      <!-- /DESKTOP APPBAR -->

      <!-- MOBILE APPBAR -->
      <template #mobile-content>
        <v-btn icon="fas fa-arrow-left" :to="originRoute.to"></v-btn>

        <div
          v-if="
            deepBoxPreviewStore.currentNodeData &&
            deepBoxPreviewStore.currentNodeData.node &&
            deepBoxPreviewStore.currentNodeData.node.displayName
          "
          class="text-body-medium text-truncate d-flex align-center justify-center"
        >
          <v-icon start>
            <NodeIcon
              :mime-type="deepBoxPreviewStore.currentNodeData.node.mimeType"
              :name="deepBoxPreviewStore.currentNodeData.node.name"
              :type="deepBoxPreviewStore.currentNodeData.node.type"
            />
          </v-icon>
          <DeepTextTruncateMiddle
            :text="deepBoxPreviewStore.currentNodeData.node.displayName"
          />
        </div>
      </template>
    </TheAppBar>
    <!-- /MOBILE APPBAR -->
    <!-- /APP BAR -->

    <DeepSupportPanel v-model="supportPanel" />

    <!-- DETAILS DRAWER -->
    <BoxDetailsDrawer
      v-if="
        isAuthenticated &&
        deepBoxPreviewStore.currentNodeData &&
        deepBoxPreviewStore.currentNodeData.deepBoxNodeId &&
        deepBoxPreviewStore.currentNodeData.boxNodeId
      "
      :box-id="deepBoxPreviewStore.currentNodeData.boxNodeId"
      :folder-node-id="parentPathSegment?.nodeId"
      hide-preview
      :is-loading="deepBoxPreviewStore.fetchParentNodeChildrenPending"
      :nodes="[deepBoxPreviewStore.currentNodeData.node]"
      :organization-id="currentContextOrgId"
      :type-id="deepBoxPreviewStore.currentNodeData.deepBoxNodeId"
      @update:comments:size="onUpdateNode({ commentCount: $event.size })"
      @update:tags="onUpdateNode({ tags: $event.tags })"
    />
    <!-- /DETAILS DRAWER -->

    <v-main>
      <PreviewBtnFabActions
        v-if="
          isAuthenticated &&
          deepBoxPreviewStore.currentNodeData &&
          canShowPreviewBtnFabActions
        "
        :box-id="deepBoxPreviewStore.currentNodeData.boxNodeId"
        :node="deepBoxPreviewStore.currentNodeData.node"
        :org-id="currentContextOrgId"
        :type-id="deepBoxPreviewStore.currentNodeData.deepBoxNodeId"
      />
      <ContainerContentOrError>
        <router-view :key="route.fullPath" />
      </ContainerContentOrError>
    </v-main>
  </v-layout>
</template>

<script lang="ts" setup>
import { useDisplay } from 'vuetify'
import { APP, BOX_FOLDERS_ORDER_DEFAULT } from '@/constants'
import helpers from '@/utils/helpers.ts'
import { useHead } from '@unhead/vue'
import last from 'lodash/last'
import { DeepTextTruncateMiddle } from '@deepcloud/deep-ui-lib'
import { sortStringToObject } from '@deepcloud/deep-ui-utils'
import { useDeepBoxBoxInfoStore } from '@/stores/deepbox/box-info.ts'
import { useDeepBoxCoreStore } from '@/stores/deepbox/core.ts'
import { useDeepBoxPreviewStore } from '@/stores/deepbox/deepboxes/preview'
import { useGlobalStore } from '@/stores/global/global'
import { DeviceKey } from '@/plugins/device-detector-js.ts'
import { isAuthenticated } from '@/composables/auth/keycloak'
import { useBox } from '@/composables/box/box'
import { createBoxBreadcrumbsContext } from '@/composables/box/box-breadcrumbs'
import { useBoxGeneric } from '@/composables/box/box-generic'
import { createContextGenericFolders } from '@/composables/context/ctx-generics'
import { useOrganization } from '@/composables/organization/organization'
import { usePreviewNavigation } from '@/composables/preview/preview-navigation'
import { usePreviewView } from '@/composables/preview/preview-view'
import TheAppBar from '@/components/appbar/TheAppBar.vue'
import BoxDetailsDrawer from '@/components/box/details-drawer/BoxDetailsDrawer.vue'
import ContainerContentOrError from '@/components/containers/ContainerContentOrError.vue'
import DeepSupportPanel from '@/components/DeepSupportPanel.vue'
import NodeIcon from '@/components/node/NodeIcon.vue'
import PreviewBtnFabActions from '@/components/preview/PreviewBtnFabActions.vue'
import type { GenericFolder, PathSegment } from '@deepcloud/api-deep-box'

const device = inject(DeviceKey)
const { mobile, mdAndUp, smAndUp } = useDisplay()
const { t, locale } = useI18n()
const route = useRoute()

const supportPanel = ref(false)

const deepBoxPreviewStore = useDeepBoxPreviewStore()
const deepBoxBoxInfoStore = useDeepBoxBoxInfoStore()
const globalStore = useGlobalStore()

onUnmounted(() => {
  deepBoxPreviewStore.$reset()
  deepBoxPreviewStore.setBackRoute(undefined)
  globalStore.setBoxDetailsDrawer(false)
})

const { isNodePreview, originRoute } = usePreviewView()
const { currentContextOrgId } = useOrganization()

useHead(() => {
  let title = `${APP.DEFAULT_TITLE}`
  if (deepBoxPreviewStore.currentNodeData?.node?.displayName) {
    title = `${deepBoxPreviewStore.currentNodeData?.node?.displayName} - ${APP.DEFAULT_TITLE}`
  }

  return {
    title,
  }
})

const parentSection = computed(() =>
  helpers.getSection(deepBoxPreviewStore.currentNodeData?.path),
)

const {
  parentChildrenFiles,
  currentFileIndex,
  displayIndex,
  totalFilesSize,
  nextItemRoute,
  previousItemRoute,
} = usePreviewNavigation()

const canShowPreviewBtnFabActions = computed(() => {
  if (
    !deepBoxPreviewStore.currentNodeData ||
    !deepBoxPreviewStore.currentNodeData.node
  )
    return false

  return Object.keys(deepBoxPreviewStore.currentNodeData.node).length !== 0
})

const deepBoxCoreStore = useDeepBoxCoreStore()
const { isBoxPrivate } = useBox(computed(() => deepBoxCoreStore.selectedBox))

const { onFetchGenericFolders } = useBoxGeneric()

const genericFolders = computedAsync<GenericFolder[]>(async () => {
  // using `locale` here to for update when `locale` is changed
  if (
    deepBoxPreviewStore.currentNodeData &&
    isBoxPrivate.value &&
    locale.value
  ) {
    const genericFolders = await onFetchGenericFolders(
      deepBoxPreviewStore.currentNodeData.deepBoxNodeId,
      deepBoxPreviewStore.currentNodeData.boxNodeId,
    )

    return genericFolders || []
  }

  return []
}, [])

createContextGenericFolders(genericFolders)

const parentPathSegment = computed(() =>
  last(deepBoxPreviewStore.currentNodeData?.path?.segments),
)

const currentGenericFolder = computed(() =>
  genericFolders.value.find(
    (g) => g.nodeId === parentPathSegment.value?.nodeId,
  ),
)

async function fetchNodes() {
  if (!deepBoxPreviewStore.currentNodeData) return
  if (!parentSection.value) return

  const children = deepBoxPreviewStore.parentNodeChildren

  // skip if we already have all nodes
  if (children && (children.nodes?.length ?? 0) >= (children.size ?? 0)) return

  let params = {}
  if (parentSection.value) {
    params = helpers.getDefaultOrderFetchParamsBySection(parentSection.value)
  }
  if (isBoxPrivate.value) {
    params = {
      ...helpers.getFetchParameters(
        sortStringToObject(BOX_FOLDERS_ORDER_DEFAULT.INBOX),
      ),
    }
  }

  if (children) {
    params = {
      ...params,
      offset: children.nodes?.length ?? 0,
    }
  }

  await deepBoxPreviewStore.fetchParentNodeChildren({
    section: parentSection.value,
    typeId: deepBoxPreviewStore.currentNodeData.deepBoxNodeId,
    boxId: deepBoxPreviewStore.currentNodeData.boxNodeId,
    nodeId: parentPathSegment.value?.nodeId ?? '',
    params,
  })
}

watch(
  () => isNodePreview.value,
  (newValue) => {
    if (newValue) {
      fetchNodes()
    }
  },
)

watch(
  () => mobile.value,
  (newValue) => {
    if (canShowPreviewBtnFabActions.value) {
      globalStore.setBoxDetailsDrawer(!newValue)
    }
  },
)

const { boxInfo } = storeToRefs(deepBoxBoxInfoStore)
const segments = ref<PathSegment[]>()
createBoxBreadcrumbsContext(segments, boxInfo)

watch(
  () => deepBoxPreviewStore.currentNodeData,
  (newValue) => {
    if (newValue) {
      if (newValue?.path?.segments) {
        segments.value = newValue?.path?.segments
      } else {
        segments.value = [{ name: 'files', nodeId: 'files' }] as PathSegment[]
      }
    }
  },
)

onMounted(() => {
  if (!mobile.value) {
    globalStore.setBoxDetailsDrawer(true)
  }
})

function onUpdateNode(changes: Record<string, unknown>) {
  const node = deepBoxPreviewStore.currentNodeData?.node
  if (!node) return

  Object.assign(node, changes)
}

// fetch next nodes
const shouldFetchNextNodes = computed(() => {
  const children = deepBoxPreviewStore.parentNodeChildren
  if (!children) return false

  const hasMore = (children.nodes?.length ?? 0) < (children.size ?? 0)
  if (!hasMore) return false

  // Case 1: current file not in loaded slice yet → must fetch more
  if (currentFileIndex.value === -1) return true

  // Case 2: approaching end of loaded slice → prefetch
  return currentFileIndex.value >= parentChildrenFiles.value.length - 3
})

const isFetchingNextNodes = ref(false)
watch(
  shouldFetchNextNodes,
  async (should) => {
    if (!should || isFetchingNextNodes.value) return

    isFetchingNextNodes.value = true
    try {
      while (shouldFetchNextNodes.value) {
        await fetchNodes()
      }
    } finally {
      isFetchingNextNodes.value = false
    }
  },
  { immediate: true },
)
</script>
